<script setup lang="ts">
  import { ref, watch, computed } from 'vue'
  import type { ResolvedField } from '../../model/types'
  import type { UpdateCustomFieldRequestPayload } from '@gat-ship/types'
  import {
    Button,
    FormField,
    FormSubmit,
    InputGroupInput,
    InputGroupNumber,
    InputGroupTextarea,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderTitle,
    ModalSection,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { FieldGroup } from '@gat-ship/vue-components'
  import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
  } from '@gat-ship/vue-components'
  import FieldEditableConfigActionCard from '../FieldEditableConfigActionCard.vue'
  import FieldVisibilityConfigActionCard from '../FieldVisibilityConfigActionCard.vue'
  import FieldValidationConfigActionCard from '../FieldValidationConfigActionCard.vue'

  const props = defineProps<{
    field: ResolvedField | null
  }>()

  const emit = defineEmits<{
    (e: 'update', payload: UpdateCustomFieldRequestPayload): void
    (e: 'delete'): void
  }>()

  const open = defineModel<boolean>('open', { default: false })

  const editedField = computed(() => props.field)

  // ── Form ──────────────────────────────────────────────────────

  const editSchema = z
    .object({
      label: z.string().min(1, 'Label is required'),
      hint: z.string(),
      aiDescription: z.string(),
      requiredOption: z.enum(['optional', 'required']),
      visibilityOption: z.enum(['visible', 'visible-for-role', 'hidden']),
      visibleForRoleIds: z.array(z.string()),
      editableOption: z.enum(['editable', 'editable-for-role', 'disabled']),
      editableForRoleIds: z.array(z.string()),
      numberOfDecimals: z.number(),
      dropdownOptions: z.string()
    })
    .superRefine((value, ctx) => {
      // `dropdownOptions` is only rendered (and only relevant) for dropdown
      // fields; `inputType` comes from the edited field, not the form values,
      // so guard presence here rather than on the field schema.
      if (
        props.field?.inputType === 'dropdown' &&
        value.dropdownOptions.trim().length === 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['dropdownOptions'],
          message: 'At least one option is required'
        })
      }
    })

  type EditFieldValues = z.infer<typeof editSchema>

  const form = useForm({
    defaultValues: {
      label: '',
      hint: '',
      aiDescription: '',
      requiredOption: 'optional',
      visibilityOption: 'visible',
      visibleForRoleIds: [],
      editableOption: 'editable',
      editableForRoleIds: [],
      numberOfDecimals: 2,
      dropdownOptions: ''
    } as EditFieldValues,
    validators: {
      onChange: editSchema
    },
    onSubmit: async ({ value }) => {
      if (!props.field) return

      const payload: UpdateCustomFieldRequestPayload = {
        label: value.label,
        hint: value.hint || null,
        aiDescription: value.aiDescription || null,
        requiredOption: value.requiredOption,
        visibilityOption: value.visibilityOption,
        visibleForRoleIds:
          value.visibilityOption === 'visible-for-role'
            ? value.visibleForRoleIds
            : null,
        editableOption: value.editableOption,
        editableForRoleIds:
          value.editableOption === 'editable-for-role'
            ? value.editableForRoleIds
            : null,
        numberOfDecimals:
          props.field.inputType === 'number' ? value.numberOfDecimals : null,
        dropdownOptions:
          props.field.inputType === 'dropdown'
            ? value.dropdownOptions.split('\n').filter(Boolean)
            : null
      }

      emit('update', payload)
      open.value = false
    }
  })

  // ── Computed ──────────────────────────────────────────────────

  const inputTypeLabel = computed(() => {
    if (!props.field?.inputType) return ''
    const labels: Record<string, string> = {
      text: 'Text',
      multiline: 'Multiline',
      number: 'Number',
      datetime: 'Date & Time',
      checkbox: 'Checkbox',
      dropdown: 'Dropdown'
    }
    return labels[props.field.inputType] ?? props.field.inputType
  })

  // ── Sync form when dialog opens ──────────────────────────────

  function populateForm(field: ResolvedField) {
    form.setFieldValue('label', field.label)
    form.setFieldValue('hint', field.hint ?? '')
    form.setFieldValue('aiDescription', field.aiDescription ?? '')
    form.setFieldValue('requiredOption', field.requiredOption)
    form.setFieldValue('visibilityOption', field.visibilityOption)
    form.setFieldValue('visibleForRoleIds', field.visibleForRoleIds ?? [])
    form.setFieldValue('editableOption', field.editableOption)
    form.setFieldValue('editableForRoleIds', field.editableForRoleIds ?? [])
    form.setFieldValue('numberOfDecimals', field.numberOfDecimals ?? 2)
    form.setFieldValue(
      'dropdownOptions',
      field.dropdownOptions?.join('\n') ?? ''
    )
  }

  function show(field: ResolvedField) {
    form.reset()
    populateForm(field)
    open.value = true
  }

  watch(open, (val) => {
    if (!val) form.reset()
  })

  const showDeleteConfirm = ref(false)

  function handleDelete() {
    open.value = false
    showDeleteConfirm.value = true
  }

  function onCancelDelete() {
    showDeleteConfirm.value = false
    open.value = true
  }

  function onConfirmDelete() {
    showDeleteConfirm.value = false
    emit('delete')
  }

  defineExpose({ show })
</script>

<template>
  <modal v-model:open="open" type="dialog">
    <ModalHeader>
      <ModalHeaderTitle>Edit "{{ editedField?.label }}"</ModalHeaderTitle>
      <ModalHeaderDescription>
        <span
          class="bg-muted inline-block rounded px-2 py-0.5 text-xs font-medium"
        >
          {{ inputTypeLabel }}
        </span>
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalSection>
      <ModalContent v-if="editedField?.inputType" class="flex flex-col gap-4">
        <FieldGroup>
          <FormField
            :form="form"
            name="label"
            label="Label"
            hint="The label on the field shown to the end user."
            required="Label is required"
          >
            <template #default="{ field: labelField }">
              <InputGroupInput
                :id="labelField.name"
                :model-value="labelField.state.value ?? ''"
                autofocus
                @update:model-value="labelField.handleChange"
                @blur="labelField.handleBlur"
              />
            </template>
          </FormField>
          <FormField
            :form="form"
            name="hint"
            label="Hint"
            hint="A tooltip hint shown to the end user - just like this one!"
          >
            <template #default="{ field: hintField }">
              <InputGroupInput
                :id="hintField.name"
                :model-value="hintField.state.value ?? ''"
                @update:model-value="hintField.handleChange"
                @blur="hintField.handleBlur"
              />
            </template>
          </FormField>
          <FormField
            :form="form"
            name="aiDescription"
            label="AI Description"
            hint="Instructions for AI on how to extract this field's value."
          >
            <template #default="{ field: descriptionField }">
              <InputGroupTextarea
                :id="descriptionField.name"
                :model-value="descriptionField.state.value ?? ''"
                :rows="2"
                @update:model-value="descriptionField.handleChange"
                @blur="descriptionField.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
        <FieldGroup v-if="editedField.inputType === 'number'">
          <FormField
            :form="form"
            name="numberOfDecimals"
            label="Number of Decimals"
          >
            <template #default="{ field: decimalsField }">
              <InputGroupNumber
                :id="decimalsField.name"
                :model-value="decimalsField.state.value"
                @update:model-value="decimalsField.handleChange"
                @blur="decimalsField.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
        <FieldGroup v-if="editedField.inputType === 'dropdown'">
          <FormField
            :form="form"
            required
            name="dropdownOptions"
            label="Dropdown Options"
            hint="One option per line."
          >
            <template #default="{ field: optionsField }">
              <InputGroupTextarea
                :id="optionsField.name"
                :model-value="optionsField.state.value ?? ''"
                :rows="4"
                @update:model-value="optionsField.handleChange"
                @blur="optionsField.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
        <h3 class="text-sm font-medium">Field Options</h3>
        <FieldValidationConfigActionCard
          v-if="editedField.inputType !== 'checkbox'"
          :form="form"
        />
        <FieldVisibilityConfigActionCard :form="form" />
        <FieldEditableConfigActionCard :form="form" />
      </ModalContent>
    </ModalSection>
    <ModalFooter>
      <Button variant="ghost" size="lg" @click="handleDelete"> Delete </Button>
      <Button class="ml-auto" variant="ghost" size="lg" @click="open = false">
        Cancel
      </Button>
      <FormSubmit :form="form" label="Save Field" />
    </ModalFooter>
  </modal>
  <AlertDialog v-model:open="showDeleteConfirm">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Delete custom field</AlertDialogTitle>
        <AlertDialogDescription>
          Are you sure you want to delete "{{ editedField?.label }}"? This
          action cannot be undone.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <Button variant="outline" @click="onConfirmDelete()"> Delete </Button>
        <Button variant="outline" @click="onCancelDelete()"> Cancel </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
